import React, { useMemo, useState } from 'react';
import {
  ArrowPathRoundedSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  Badge,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Textarea,
} from '../ui';
import {
  useCreateWorkspaceSemanticCorrectionProposal,
  useDeleteWorkspaceSemanticCorrectionProposal,
  useReviewWorkspaceSemanticCorrectionProposal,
  useRouteWorkspaceSemanticCorrectionProposal,
  useWorkspaceSemanticCorrectionProposals,
  type SemanticCorrectionKind,
  type SemanticCorrectionSourceSurface,
  type SemanticCorrectionTargetScope,
  type WorkspaceSemanticCorrectionProposal,
} from '../../hooks/useWorkspaceSemanticContext';
import type { StructuredAnalyticsDraft } from './structuredAnalyticsConfig';

const SOURCE_OPTIONS: Array<{ value: SemanticCorrectionSourceSurface; label: string }> = [
  { value: 'chat', label: 'Chat' },
  { value: 'mcp', label: 'MCP' },
  { value: 'catalog', label: 'Catalog' },
  { value: 'ontology_builder', label: 'Ontology builder' },
  { value: 'system', label: 'System' },
];

const SCOPE_OPTIONS: Array<{ value: SemanticCorrectionTargetScope; label: string }> = [
  { value: 'workspace', label: 'Workspace ontology' },
  { value: 'catalog', label: 'Catalog review' },
  { value: 'triage', label: 'Triage' },
];

const KIND_OPTIONS: Array<{ value: SemanticCorrectionKind; label: string }> = [
  { value: 'ontology_alias', label: 'Ontology alias' },
  { value: 'ontology_metric', label: 'Ontology metric' },
  { value: 'ontology_entity', label: 'Ontology entity' },
  { value: 'ontology_relationship', label: 'Ontology relationship' },
  { value: 'ontology_default_filter', label: 'Default filter' },
  { value: 'warehouse_column_alias', label: 'Column alias' },
  { value: 'catalog_asset_metadata', label: 'Catalog asset metadata' },
  { value: 'catalog_column_metadata', label: 'Catalog column metadata' },
  { value: 'runtime_feedback', label: 'Runtime feedback' },
  { value: 'unknown', label: 'Unknown' },
];

const EMPTY_PROPOSALS: WorkspaceSemanticCorrectionProposal[] = [];

interface MetricDraft {
  name: string;
  display_name: string;
  description: string;
  sql_formula: string;
  time_window: string;
  grain: string;
  aliases: string;
  tables: string;
  columns: string;
  filters: string;
  join_hints: string;
  unit: string;
  format_type: string;
}

export interface WorkspaceCorrectionsSectionProps {
  workspaceId: number;
  draft: StructuredAnalyticsDraft;
  onOpenCatalogConnections: () => void;
}

function selectClassName() {
  return [
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-charcoal shadow-sm',
    'focus:border-eliza-red focus:outline-none focus:ring-2 focus:ring-eliza-red/20',
    'dark:border-dark-border dark:bg-dark-surface dark:text-gray-100',
  ].join(' ');
}

function formatDate(value?: string | null) {
  if (!value) return 'Not recorded';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function statusVariant(status: string): React.ComponentProps<typeof Badge>['variant'] {
  if (status === 'pending') return 'warning';
  if (status === 'rejected') return 'danger';
  if (status.startsWith('applied') || status === 'approved') return 'success';
  return 'secondary';
}

function isPermissionError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || '');
  return message.includes('403') || message.toLowerCase().includes('forbidden');
}

function targetRefSummary(proposal: WorkspaceSemanticCorrectionProposal) {
  const targetRef = proposal.target_ref;
  if (!targetRef || Object.keys(targetRef).length === 0) return 'No target ref';
  return Object.entries(targetRef)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}

function humanizeToken(value: string | number | null | undefined): string {
  return String(value ?? '').replace(/_/g, ' ');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function recordString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function listString(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'string') return value;
  return '';
}

function metricDraftFromProposal(proposal: WorkspaceSemanticCorrectionProposal): MetricDraft {
  const patch = isRecord(proposal.proposed_patch) ? proposal.proposed_patch : {};
  const metric = isRecord(patch.metric) ? patch.metric : patch;
  const metadata = isRecord(metric.expression_metadata) ? metric.expression_metadata : {};
  const displayName = recordString(metric, 'display_name') || recordString(metric, 'name');
  return {
    name: recordString(metric, 'name'),
    display_name: displayName,
    description: recordString(metric, 'description') || recordString(metadata, 'definition') || proposal.user_text || '',
    sql_formula: recordString(metric, 'sql_formula'),
    time_window: recordString(metadata, 'time_window'),
    grain: recordString(metadata, 'grain'),
    aliases: listString(metadata.aliases),
    tables: listString(metadata.tables),
    columns: listString(metadata.columns),
    filters: typeof metadata.filters === 'string' ? metadata.filters : isRecord(metadata.filters) ? JSON.stringify(metadata.filters) : '',
    join_hints: typeof metadata.join_hints === 'string' ? metadata.join_hints : isRecord(metadata.join_hints) ? JSON.stringify(metadata.join_hints) : '',
    unit: recordString(metric, 'unit'),
    format_type: recordString(metric, 'format_type'),
  };
}

function splitList(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function metricPatchFromDraft(draft: MetricDraft): Record<string, unknown> {
  const expression_metadata: Record<string, unknown> = {
    definition: draft.description.trim() || undefined,
    time_window: draft.time_window.trim() || undefined,
    grain: draft.grain.trim() || undefined,
    aliases: splitList(draft.aliases),
    tables: splitList(draft.tables),
    columns: splitList(draft.columns),
    filters: draft.filters.trim() || undefined,
    join_hints: draft.join_hints.trim() || undefined,
  };
  Object.keys(expression_metadata).forEach((key) => {
    const value = expression_metadata[key];
    if (value === undefined || (Array.isArray(value) && value.length === 0)) delete expression_metadata[key];
  });
  const metric: Record<string, unknown> = {
    name: draft.name.trim() || undefined,
    display_name: draft.display_name.trim() || undefined,
    description: draft.description.trim() || undefined,
    sql_formula: draft.sql_formula.trim() || undefined,
    unit: draft.unit.trim() || undefined,
    format_type: draft.format_type.trim() || undefined,
    expression_metadata,
  };
  Object.keys(metric).forEach((key) => {
    const value = metric[key];
    if (value === undefined || (isRecord(value) && Object.keys(value).length === 0)) delete metric[key];
  });
  return { metric };
}

function ProposalRow({
  proposal,
  onReview,
  onDelete,
  onRoute,
  isBusy,
}: {
  proposal: WorkspaceSemanticCorrectionProposal;
  onReview: (
    proposalId: number,
    action: 'approve' | 'reject',
    proposedPatch?: Record<string, unknown>,
  ) => void;
  onDelete: (proposal: WorkspaceSemanticCorrectionProposal) => void;
  onRoute: (proposalId: number, targetScope: SemanticCorrectionTargetScope) => void;
  isBusy: boolean;
}) {
  const [routeScope, setRouteScope] = useState<SemanticCorrectionTargetScope>(
    proposal.target_scope === 'catalog' || proposal.target_scope === 'triage'
      ? proposal.target_scope
      : 'workspace',
  );
  const [metricDraft, setMetricDraft] = useState<MetricDraft>(() => metricDraftFromProposal(proposal));
  const reviewable = proposal.status === 'pending' || proposal.status === 'needs_clarification';
  const isMetricProposal = proposal.correction_kind === 'ontology_metric' && proposal.target_scope === 'workspace';

  function updateMetricDraft(field: keyof MetricDraft, value: string) {
    setMetricDraft((current) => ({ ...current, [field]: value }));
  }

  return (
    <article className="border-t border-gray-200 px-5 py-4 dark:border-dark-border">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(proposal.status)}>{humanizeToken(proposal.status)}</Badge>
            <Badge variant="secondary">{humanizeToken(proposal.target_scope)}</Badge>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {humanizeToken(proposal.source_surface)} · pack{' '}
              {proposal.active_pack_version_at_submission ?? 'none'} · {formatDate(proposal.created_at)}
            </span>
          </div>
          <h4 className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">
            {proposal.user_text || proposal.proposed_value || 'Correction proposal'}
          </h4>
          <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
            {humanizeToken(proposal.correction_kind)}
            {proposal.field_path ? ` · ${proposal.field_path}` : ''}
            {proposal.proposed_value ? ` · proposed: ${proposal.proposed_value}` : ''}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{targetRefSummary(proposal)}</p>
          {proposal.reviewer_notes ? (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Review notes: {proposal.reviewer_notes}
            </p>
          ) : null}
          {proposal.routed_catalog_correction_id ? (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Routed to catalog correction #{proposal.routed_catalog_correction_id}
            </p>
          ) : null}
          {isMetricProposal ? (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor={`metric-display-name-${proposal.id}`}>Metric display name</Label>
                  <Input
                    id={`metric-display-name-${proposal.id}`}
                    value={metricDraft.display_name}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('display_name', event.target.value)}
                    placeholder="Stay-and-play conversion rate"
                  />
                </div>
                <div>
                  <Label htmlFor={`metric-name-${proposal.id}`}>Metric key</Label>
                  <Input
                    id={`metric-name-${proposal.id}`}
                    value={metricDraft.name}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('name', event.target.value)}
                    placeholder="stay_and_play_conversion_rate"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor={`metric-description-${proposal.id}`}>Definition</Label>
                <Textarea
                  id={`metric-description-${proposal.id}`}
                  value={metricDraft.description}
                  disabled={!reviewable || isBusy}
                  onChange={(event) => updateMetricDraft('description', event.target.value)}
                  rows={3}
                />
              </div>
              <div className="mt-3">
                <Label htmlFor={`metric-sql-${proposal.id}`}>SQL formula</Label>
                <Textarea
                  id={`metric-sql-${proposal.id}`}
                  value={metricDraft.sql_formula}
                  disabled={!reviewable || isBusy}
                  onChange={(event) => updateMetricDraft('sql_formula', event.target.value)}
                  placeholder="COUNT(DISTINCT CASE WHEN condition THEN loyalty_id END)"
                  rows={3}
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div>
                  <Label htmlFor={`metric-window-${proposal.id}`}>Time window</Label>
                  <Input
                    id={`metric-window-${proposal.id}`}
                    value={metricDraft.time_window}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('time_window', event.target.value)}
                    placeholder="72 hours after check-in"
                  />
                </div>
                <div>
                  <Label htmlFor={`metric-unit-${proposal.id}`}>Unit</Label>
                  <Input
                    id={`metric-unit-${proposal.id}`}
                    value={metricDraft.unit}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('unit', event.target.value)}
                    placeholder="percentage"
                  />
                </div>
                <div>
                  <Label htmlFor={`metric-format-${proposal.id}`}>Format</Label>
                  <Input
                    id={`metric-format-${proposal.id}`}
                    value={metricDraft.format_type}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('format_type', event.target.value)}
                    placeholder="percent"
                  />
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div>
                  <Label htmlFor={`metric-aliases-${proposal.id}`}>Aliases</Label>
                  <Input
                    id={`metric-aliases-${proposal.id}`}
                    value={metricDraft.aliases}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('aliases', event.target.value)}
                    placeholder="stay-and-play, conversion"
                  />
                </div>
                <div>
                  <Label htmlFor={`metric-tables-${proposal.id}`}>Tables</Label>
                  <Input
                    id={`metric-tables-${proposal.id}`}
                    value={metricDraft.tables}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('tables', event.target.value)}
                    placeholder="hotel_stays, casino_activity"
                  />
                </div>
                <div>
                  <Label htmlFor={`metric-columns-${proposal.id}`}>Columns</Label>
                  <Input
                    id={`metric-columns-${proposal.id}`}
                    value={metricDraft.columns}
                    disabled={!reviewable || isBusy}
                    onChange={(event) => updateMetricDraft('columns', event.target.value)}
                    placeholder="guest_id, check_in_date"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          <div className="flex gap-2">
            <select
              aria-label={`Route proposal ${proposal.id}`}
              className={`${selectClassName()} min-w-[170px]`}
              disabled={!reviewable || isBusy}
              value={routeScope}
              onChange={(event) => setRouteScope(event.target.value as SemanticCorrectionTargetScope)}
            >
              {SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!reviewable || isBusy || routeScope === proposal.target_scope}
              onClick={() => onRoute(proposal.id, routeScope)}
              title="Route proposal"
            >
              <ArrowPathRoundedSquareIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!reviewable || isBusy}
              onClick={() => onReview(proposal.id, 'reject')}
            >
              <XCircleIcon className="mr-2 h-4 w-4" />
              Reject
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => onDelete(proposal)}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!reviewable || isBusy}
              onClick={() => onReview(
                proposal.id,
                'approve',
                isMetricProposal ? metricPatchFromDraft(metricDraft) : undefined,
              )}
            >
              <CheckCircleIcon className="mr-2 h-4 w-4" />
              Approve
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

export function WorkspaceCorrectionsSection({
  workspaceId,
  draft,
  onOpenCatalogConnections,
}: WorkspaceCorrectionsSectionProps) {
  const proposalsQuery = useWorkspaceSemanticCorrectionProposals(workspaceId);
  const createProposal = useCreateWorkspaceSemanticCorrectionProposal(workspaceId);
  const deleteProposal = useDeleteWorkspaceSemanticCorrectionProposal(workspaceId);
  const reviewProposal = useReviewWorkspaceSemanticCorrectionProposal(workspaceId);
  const routeProposal = useRouteWorkspaceSemanticCorrectionProposal(workspaceId);
  const [sourceSurface, setSourceSurface] = useState<SemanticCorrectionSourceSurface>('chat');
  const [targetScope, setTargetScope] = useState<SemanticCorrectionTargetScope>('workspace');
  const [correctionKind, setCorrectionKind] = useState<SemanticCorrectionKind>('ontology_alias');
  const [fieldPath, setFieldPath] = useState('');
  const [proposedValue, setProposedValue] = useState('');
  const [userText, setUserText] = useState('');
  const [targetRefJson, setTargetRefJson] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isRaiseOpen, setIsRaiseOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceSemanticCorrectionProposal | null>(null);

  const catalogWarnings = draft.blockers.filter((blocker) =>
    String(blocker.code || '').startsWith('catalog_enrichment_'),
  );
  const proposals = proposalsQuery.data?.proposals ?? EMPTY_PROPOSALS;
  const pendingCount = useMemo(
    () => proposals.filter((proposal) => proposal.status === 'pending').length,
    [proposals],
  );
  const isMutating = createProposal.isPending || reviewProposal.isPending || routeProposal.isPending || deleteProposal.isPending;

  async function submitProposal(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    let targetRef: Record<string, unknown> | null = null;
    if (targetRefJson.trim()) {
      try {
        targetRef = JSON.parse(targetRefJson) as Record<string, unknown>;
      } catch {
        setFormError('Target refs must be valid JSON, such as {"ontology_entity_id": 12}.');
        return;
      }
    }
    try {
      await createProposal.mutateAsync({
        source_surface: sourceSurface,
        target_scope: targetScope,
        correction_kind: correctionKind,
        field_path: fieldPath.trim() || null,
        proposed_value: proposedValue.trim() || null,
        user_text: userText.trim() || null,
        target_ref: targetRef,
      });
      setFieldPath('');
      setProposedValue('');
      setUserText('');
      setTargetRefJson('');
      setIsRaiseOpen(false);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to create correction proposal.');
    }
  }

  async function handleReview(
    proposalId: number,
    action: 'approve' | 'reject',
    proposedPatch?: Record<string, unknown>,
  ) {
    await reviewProposal.mutateAsync({
      proposalId,
      action,
      reviewer_notes: null,
      proposed_patch: action === 'approve' ? proposedPatch : undefined,
    });
  }

  async function handleRoute(proposalId: number, nextScope: SemanticCorrectionTargetScope) {
    await routeProposal.mutateAsync({
      proposalId,
      route: {
        target_scope: nextScope,
        reviewer_notes: `Routed from workspace corrections UI to ${nextScope}.`,
      },
    });
  }

  async function handleDeleteConfirmed() {
    if (!deleteTarget) return;
    await deleteProposal.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  }

  return (
    <section
      id="semantic-corrections"
      className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-[hsl(220_25%_18%)] dark:bg-[hsl(217_33%_12%)]"
    >
      <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 dark:border-dark-border lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 gap-4">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
            <InboxIcon className="h-7 w-7 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Corrections
              <Badge variant="secondary">Workspace queue</Badge>
              <Badge variant={pendingCount ? 'warning' : 'secondary'}>{pendingCount} pending</Badge>
            </div>
            <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-white">
              Governed semantic correction proposals
            </h3>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Corrections are captured for steward review with the active pack version at submission. Approval can
              route catalog work to Catalog or apply supported workspace ontology draft edits; runtime still waits for a
              compiled and activated pack.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onOpenCatalogConnections}>
            Catalog admin
          </Button>
          <Button type="button" size="sm" onClick={() => setIsRaiseOpen(true)}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Raise correction
          </Button>
        </div>
      </div>

      {catalogWarnings.length > 0 ? (
        <div className="border-b border-gray-200 dark:border-dark-border">
          {catalogWarnings.map((warning, index) => (
            <div key={`${warning.code || 'catalog-warning'}-${index}`} className="flex items-start gap-3 px-5 py-4">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-charcoal dark:text-gray-100">Metadata coverage warning</p>
                  <Badge variant="warning">Non-blocking</Badge>
                </div>
                <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{warning.message}</p>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {proposalsQuery.isLoading ? (
        <div className="flex items-center justify-center gap-3 px-5 py-10 text-sm text-gray-500 dark:text-gray-400">
          <Spinner size="sm" />
          Loading correction proposals
        </div>
      ) : proposalsQuery.isError ? (
        <div className="px-5 py-10 text-center">
          <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-amber-500" />
          <h4 className="mt-3 text-sm font-semibold text-charcoal dark:text-gray-100">
            {isPermissionError(proposalsQuery.error) ? 'Permission required' : 'Correction queue unavailable'}
          </h4>
          <p className="mx-auto mt-1 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
            {isPermissionError(proposalsQuery.error)
              ? 'This workspace queue requires workspace semantic access.'
              : 'The semantic-context correction proposal API did not return a queue.'}
          </p>
        </div>
      ) : proposals.length > 0 ? (
        <div>
          {proposals.map((proposal) => (
            <ProposalRow
              key={proposal.id}
              proposal={proposal}
              isBusy={isMutating}
              onDelete={setDeleteTarget}
              onReview={handleReview}
              onRoute={handleRoute}
            />
          ))}
        </div>
      ) : (
        <div className="px-5 py-10 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
            <InboxIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <h4 className="mt-3 text-sm font-semibold text-charcoal dark:text-gray-100">No corrections raised</h4>
          <p className="mx-auto mt-1 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
            New chat, MCP, catalog, ontology builder, or system feedback can be captured here before any runtime
            behavior changes.
          </p>
        </div>
      )}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>Delete correction request?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
              This removes the correction request from the semantic correction queue. It will not change ontology,
              catalog, or runtime pack data.
            </p>
            {deleteTarget ? (
              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
                <p className="line-clamp-3 text-sm font-medium text-charcoal dark:text-gray-100">
                  {deleteTarget.user_text || deleteTarget.proposed_value || 'Correction request'}
                </p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {humanizeToken(deleteTarget.correction_kind)} · {humanizeToken(deleteTarget.target_scope)}
                </p>
              </div>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={deleteProposal.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={deleteProposal.isPending}
              onClick={handleDeleteConfirmed}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete request
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal open={isRaiseOpen} onClose={() => setIsRaiseOpen(false)}>
        <ModalContent size="full" className="max-h-[90vh] overflow-hidden">
          <ModalHeader>
            <ModalTitle>Raise correction</ModalTitle>
          </ModalHeader>
          <form onSubmit={submitProposal}>
            <ModalBody className="max-h-[calc(90vh-9rem)] overflow-y-auto">
              <div className="grid gap-4 lg:grid-cols-3">
                <div>
                  <Label htmlFor="correction-source">Source</Label>
                  <select
                    id="correction-source"
                    className={`mt-1 w-full ${selectClassName()}`}
                    value={sourceSurface}
                    onChange={(event) => setSourceSurface(event.target.value as SemanticCorrectionSourceSurface)}
                  >
                    {SOURCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="correction-scope">Route</Label>
                  <select
                    id="correction-scope"
                    className={`mt-1 w-full ${selectClassName()}`}
                    value={targetScope}
                    onChange={(event) => setTargetScope(event.target.value as SemanticCorrectionTargetScope)}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="correction-kind">Kind</Label>
                  <select
                    id="correction-kind"
                    className={`mt-1 w-full ${selectClassName()}`}
                    value={correctionKind}
                    onChange={(event) => setCorrectionKind(event.target.value as SemanticCorrectionKind)}
                  >
                    {KIND_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <div>
                  <Label htmlFor="correction-field-path">Field path</Label>
                  <Input
                    id="correction-field-path"
                    value={fieldPath}
                    onChange={(event) => setFieldPath(event.target.value)}
                    placeholder="description"
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label htmlFor="correction-target-ref">Target refs JSON</Label>
                  <Input
                    id="correction-target-ref"
                    value={targetRefJson}
                    onChange={(event) => setTargetRefJson(event.target.value)}
                    placeholder='{"ontology_entity_id": 12}'
                  />
                </div>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                  <Label htmlFor="correction-user-text">Correction text</Label>
                  <Textarea
                    id="correction-user-text"
                    value={userText}
                    onChange={(event) => setUserText(event.target.value)}
                    placeholder="By money I meant casino cash out."
                    rows={4}
                  />
                </div>
                <div>
                  <Label htmlFor="correction-proposed-value">Proposed value</Label>
                  <Textarea
                    id="correction-proposed-value"
                    value={proposedValue}
                    onChange={(event) => setProposedValue(event.target.value)}
                    placeholder="Use cash_out when the workspace says money."
                    rows={4}
                  />
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                Manual submission is explicit confirmation that this should be remembered for steward review.
              </p>
              {formError ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
            </ModalBody>
            <ModalFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setIsRaiseOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isMutating}>
                <PlusIcon className="mr-2 h-4 w-4" />
                Raise correction
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </section>
  );
}
